import {
  BinaryChunkResponse,
  EndResponse,
  ErrorResponse,
  InputMode,
  PongResponse,
  ResponseMode,
  TextResponse,
} from "./types";
import { Mic, Type, Volume2, MessageSquare } from "lucide-react";

export function decodeBase64(encoded: string) {
  const binaryString = atob(encoded);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}

export const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

export function isTextResponse(data: unknown): data is TextResponse {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as TextResponse).type === "text"
  );
}

export function isBinaryChunkResponse(
  data: unknown
): data is BinaryChunkResponse {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as BinaryChunkResponse).type === "binary-chunk"
  );
}

function isErrorResponse(data: unknown): data is ErrorResponse {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as ErrorResponse).type === "error" &&
    typeof (data as ErrorResponse).message === "string"
  );
}

function isPongResponse(data: unknown): data is PongResponse {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as PongResponse).type === "pong" &&
    (data as PongResponse).pong === "pong"
  );
}

function isEndResponse(data: unknown): data is EndResponse {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as EndResponse).type === "end"
  );
}

export const getText = (data: unknown) =>
  isTextResponse(data) ? data.result : undefined;
export const getBinaryChunk = (data: unknown) =>
  isBinaryChunkResponse(data) ? data.value : undefined;
export const getErrorMessage = (data: unknown) =>
  isErrorResponse(data) ? data.message : undefined;
export const isError = (data: unknown) => isErrorResponse(data);
export const isPong = (data: unknown) => isPongResponse(data);
export const isEnd = (data: unknown) => isEndResponse(data);

export function nodeStreamToWebReadable(nodeStream: any) {
  return new ReadableStream({
    start(controller) {
      nodeStream.on("data", (chunk: any) => {
        controller.enqueue(chunk);
      });
      nodeStream.on("end", () => {
        controller.close();
      });
      nodeStream.on("error", (err: any) => {
        controller.error(err);
      });
    },
  });
}

export function getIsMediaSourceSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.MediaSource !== "undefined" &&
    typeof MediaSource.isTypeSupported === "function"
  );
}

export enum FibiAiQuery {
  userType = "userType",
}

export enum FibiAiQueryValue {
  fibiUser = "fibiUser",
}

export const inputModes: {
  value: InputMode;
  label: string;
  shortLabel: string;
  icon: React.ReactNode;
  description: string;
}[] = [
  {
    value: "voice-only",
    label: "Voice Only",
    shortLabel: "Voice",
    icon: <Mic className="w-4 h-4" />,
    description:
      "Speak directly to the AI using your microphone. No text input field will be available. Perfect for hands-free interaction.",
  },
  {
    value: "text-only",
    label: "Text Only",
    shortLabel: "Text",
    icon: <Type className="w-4 h-4" />,
    description:
      "Type your messages using the keyboard. Microphone will be disabled. Ideal for quiet environments or when voice input isn't available.",
  },
  {
    value: "voice-text",
    label: "Voice + Text",
    shortLabel: "Both",
    icon: <MessageSquare className="w-4 h-4" />,
    description:
      "Use both voice and text input methods. Switch between typing and speaking as needed for maximum flexibility.",
  },
];

export const responseModes: {
  value: ResponseMode;
  label: string;
  shortLabel: string;
  icon: React.ReactNode;
  description: string;
}[] = [
  {
    value: "text-only",
    label: "Text Only",
    shortLabel: "Text",
    icon: <Type className="w-4 h-4" />,
    description:
      "AI responses will appear as text messages only. Silent operation - perfect for quiet environments or when audio output isn't desired.",
  },
  {
    value: "text-voice",
    label: "Text + Voice",
    shortLabel: "Text + Voice",
    icon: <Volume2 className="w-4 h-4" />,
    description:
      "AI responses will be displayed as text and also spoken aloud using text-to-speech. Great for accessibility and multitasking scenarios.",
  },
];
